<template>
  <div class="history-panel">
    <h2>History</h2>
    <div v-if="isGetHistoryRequestPending" class="history-loading">
      <div class="loading-content">
        <p class="loading-text">Loading history...</p>
      </div>
    </div>
    <div v-else-if="isGetHistoryRequestFailed" class="history-error">
      <p>Failed to load history</p>
    </div>
    <div v-else-if="skillHistory && skillHistory.length" class="history-list">
      <div v-for="commit in skillHistory" :key="commit.version" class="history-item" :class="{ 'is-current': commit.version === currentVersion }">
        <div class="history-item-header">
          <span class="version">v{{ commit.version }}<span v-if="commit.version === currentVersion" class="current-badge"> (current)</span></span>
          <span class="date">{{ formatDate(commit.lastModified) }}</span>
        </div>
        <div class="history-item-body">
          <p class="message" :class="{ 'no-message': !commit.message }">{{ commit.message || 'No message' }}</p>
          <div class="author-row">
            <p class="author">by {{ commit.lastModifiedBy || 'Unknown' }}</p>
            <div class="history-item-actions">
              <p-button v-if="$hasPermission('skills') && commit.version !== currentVersion" size="small" color="secondary" title="Delete" @click="deleteVersion(commit)">
                <p-md-icon name="delete" />
              </p-button>
              <p-button v-if="commit.version !== currentVersion" size="small" color="primary" title="Compare" @click="compareVersion(commit)">
                <p-md-icon name="file-compare" />
              </p-button>
              <p-button size="small" title="Preview" @click="previewVersion(commit)">
                <p-md-icon name="eye" />
              </p-button>
              <p-button v-if="$hasPermission('skills') && commit.version !== currentVersion" size="small" title="Activate" @click="activateVersion(commit)">
                <p-md-icon name="play" />
              </p-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="history-empty">
      <p>No history available</p>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Button from '@/components/common/Button';
import MdIcon from '@/components/common/MdIcon';

export default {
  components: {
    'p-button': Button,
    'p-md-icon': MdIcon
  },
  props: {
    skillName: {
      type: String,
      required: true
    },
    currentVersion: {
      type: Number,
      default: null
    }
  },
  computed: {
    ...mapState({
      isGetHistoryRequestPending: s => s.nlp.getSkillHistoryPending,
      isGetHistoryRequestFailed: s => s.nlp.getSkillHistoryFailed,
      skillHistory: s => s.nlp.skillHistory
    })
  },
  async created() {
    await this.$store.dispatch('nlp/getSkillHistory', this.skillName);
  },
  methods: {
    formatDate(value) {
      if (!value) return '';
      try {
        const date = new Date(value);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (e) {
        return value;
      }
    },
    async activateVersion(commit) {
      const confirm = await this.$confirm({
        title: `Activate version ${commit.version}?`,
        message: `This will create a new version based on v${commit.version}.`,
        confirm: 'Activate'
      });

      if (!confirm) {
        return;
      }

      const lock = this.$lock();
      try {
        await this.$store.dispatch('nlp/updateSkill', {
          name: commit.name,
          skill: {
            description: commit.description,
            displayDescription: commit.displayDescription,
            content: commit.content,
            scope: commit.scope,
            tags: commit.tags,
            organizations: commit.organizations,
            models: commit.models,
            message: `Activated from v${commit.version}`,
            sourceVersion: commit.version
          }
        });

        this.$toast.success({
          title: 'Version activated',
          message: `Version ${commit.version} has been activated as a new version.`
        });

        await this.$store.dispatch('nlp/getSkillHistory', this.skillName);
        this.$emit('version-activated');
      } catch (e) {
        const serverMessage = await e?.response?.text?.().then(t => { try { return JSON.parse(t).message; } catch { return null; } }).catch(() => null);
        this.$toast.error({
          title: 'Failed to activate version',
          message: serverMessage || 'Please try again later.'
        });
      } finally {
        lock.release();
      }
    },
    async deleteVersion(commit) {
      const confirm = await this.$confirm({
        title: `Delete version ${commit.version}?`,
        message: `Are you sure you want to delete v${commit.version}?\n\nThis action cannot be undone.`,
        confirm: 'Delete'
      });

      if (!confirm) {
        return;
      }

      const lock = this.$lock();
      try {
        await this.$store.dispatch('nlp/deleteSkillVersion', {
          name: this.skillName,
          version: commit.version
        });

        this.$toast.success({
          title: 'Version deleted',
          message: `Version ${commit.version} has been deleted.`
        });

        this.$emit('version-deleted', commit.version);
      } catch (e) {
        this.$toast.error({
          title: 'Failed to delete version',
          message: 'Please try again later.'
        });
      } finally {
        lock.release();
      }
    },
    compareVersion(commit) {
      this.$emit('compare-version', commit);
    },
    previewVersion(commit) {
      this.$emit('preview-version', commit);
    }
  }
};
</script>

<style lang="scss" scoped>
.history-panel {
  background: var(--theme-surface);
  border-radius: 0;
  padding: 0.5rem;
  border: none;
  border-left: 1px solid var(--theme-on-background-dark);
  height: 100%;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  h2 {
    color: var(--theme-on-surface);
    font-size: 0.85rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
    flex-shrink: 0;
  }

  .history-loading,
  .history-error,
  .history-empty {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    color: var(--theme-on-surface);
    opacity: 0.6;
    flex: 1;

    .loading-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;

      .loading-text {
        margin: 0;
        font-size: 0.8rem;
        color: var(--theme-on-surface);
      }
    }
  }

  .history-list {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: 0.5rem;
    padding-right: 0.5rem;
  }

  .history-item {
    background: var(--theme-background);
    border: 1px solid var(--theme-on-background-dark);
    border-radius: 4px;
    padding: 0.35rem 0.4rem;
    transition: border-color 0.2s ease;
    flex-shrink: 0;

    &.is-current {
      border-color: var(--theme-primary);
      background: var(--theme-surface);
    }

    &:hover {
      border-color: var(--theme-primary);

      .history-item-actions {
        opacity: 1;
      }
    }

    .history-item-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.25rem;

      .version {
        font-weight: 600;
        color: var(--theme-primary);
        font-size: 0.8rem;

        .current-badge {
          font-weight: 500;
          font-size: 0.7rem;
          opacity: 0.8;
        }
      }

      .date {
        font-size: 0.7rem;
        color: var(--theme-on-surface);
        opacity: 0.7;
      }
    }

    .history-item-body {
      .message {
        color: var(--theme-on-surface);
        font-size: 0.73rem;
        margin: 0 0 0.15rem 0;
        word-wrap: break-word;
        line-height: 1.2;
        opacity: 0.5;
        font-style: italic;

        &.no-message {
          opacity: 0.25;
          font-size: 0.7rem;
        }
      }

      .author-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.4rem;
      }

      .author {
        font-size: 0.7rem;
        color: var(--theme-on-surface);
        opacity: 0.6;
        margin: 0;
        font-style: italic;
      }
    }

    .history-item-actions {
      display: flex;
      gap: 0.3rem;
      opacity: 0;
      transition: opacity 0.2s ease;
      flex-wrap: wrap;

      ::v-deep button {
        font-size: 0.65rem;
        padding: 0.2rem;
        min-height: auto;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
}
</style>
